import React, { useContext, useState } from 'react';

import { useNavigate } from '@plasma/ui.application.router';
import ThemeProvider from '@plasma/ui.themes.theme-provider';
import { useTranslation } from '@plasma/ui.utils.i18n';
import { ThemeOverride } from '@plasma/ui.utils.types';

import { PlantLayoutContext } from '../..';
import LoadingComponentScalable from '../Loding-component/LoadingComponentMini';
import BoxMenuItem from './BoxMenuItem';
import { Container, Header, BoxMenuContainer, BoxMenuRow, StyledIcon } from './mainHome.style';
import themes from './themes';

interface MainHomeProps {
  boxSize: { height: string; width: string };
}

type Props = MainHomeProps & ThemeOverride;

const MainHome: React.FC<Props> = ({ boxSize, themeMode }) => {
  const UserRoles = JSON.parse(sessionStorage.getItem('UserRoles') || '');
  const isAdmin = sessionStorage.getItem('superUserRole');

  const [displaySettings, setDisplaySettings] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // Destructure layoutData and isLoading from the context object
  const plantLayoutContext = useContext(PlantLayoutContext);

  // Set the loading indicator safely based directly on the explicit boolean flag
  const isLayoutLoading = plantLayoutContext ? plantLayoutContext.isLoading : false;

  const headings = [{ header: 'SMS WEAR MANAGER', subHeader: t('home-screen') }];

  const boxMenuConfig = [
    { icon: 'warehouse', titleKey: 'plant-layout-and-area-drawing', route: '/plant-layout-area-drawings' },
    { icon: 'webhook', titleKey: 'roll-pass-design', route: '/role-base-design' },
    { icon: 'miscellaneous_services', titleKey: 'consumable-overview-and-utilization', route: '/consumable-overview' },
    { icon: 'find_in_page', titleKey: 'item-and-document-finder', route: '/item-document-finder' },
    { icon: 'info_outline', titleKey: 'profile-detail', route: '/product-detail' },
    { icon: 'balance', titleKey: 'production-plan', route: '/production-figure' },
    { icon: 'shopping_cart', titleKey: 'shopping-cart', route: '/shopping-cart/0' },
    { icon: 'troubleshoot', titleKey: 'current-rfq-status', route: '/current-rfq-status' },
    { icon: 'update', titleKey: 'consumable-lifetime-cycle', route: '/consumable-lifetime-cycle' },
    // { icon: 'shopping_bag_speed', titleKey: 'guide-order', route: '/guide-order' },
    { icon: 'settings', titleKey: 'settings', route: '/settings' },
    { icon: 'support_agent', titleKey: 'service-support', route: '/faq-service-support' },
    // { icon: 'category', titleKey: 'profile-families', route: '/product-families' },
  ];

  const boxMenuSettingsConfig = [
    { icon: 'add_task', titleKey: 'select-create-project', route: '/create-new-project' },
    { icon: 'business', titleKey: 'manage-customer-plant', route: '/all-companies' },
    { icon: 'file_upload', titleKey: 'upload-plant-config', route: '/upload-excel' },
    { icon: 'groups_1', titleKey: 'manage-users', route: '/users-page' },
    { icon: 'groups_2', titleKey: 'manage-personnel', route: '/all-personnel' },
    { icon: 'category', titleKey: 'associate-personnel', route: '/associated-personnel' },
  ];

  const renderBoxMenus = (
    configArray: { icon: string; titleKey: string; route: string }[],
    startIndex: number,
    count: number,
  ) => {
    const slicedArray = configArray.slice(startIndex, startIndex + count);

    return slicedArray.map((config) => {
      // Extract the route part that matches the role
      const routeRole = config.route.replace(/^\//, '').replace(/\/\d*$/, '');

      // Check if the user has the required role
      //console.log('is admin: ', isAdmin);
      const hasRole = UserRoles.includes(routeRole) || isAdmin;

      return (
        <BoxMenuItem
          key={config.titleKey}
          boxSize={boxSize}
          icon={config.icon}
          iconSize="55"
          title={
            <div>
              <div>{t(config.titleKey)}</div>
              {/* {!hasRole && <div>Coming soon...</div>} */}
            </div>
          }
          titleFontSize="14"
          onBoxClick={() => (hasRole ? navigateFromBox(config.route) : null)}
          disabled={!hasRole || isLayoutLoading}
        />
      );
    });
  };

  const navigateFromBox = (route: string) => {
    if (route !== `/settings`) {
      navigate(route);
      setDisplaySettings(false);
    } else {
      setDisplaySettings(true);
    }
  };

  return (
    <ThemeProvider themes={themes} selectedMode={themeMode}>
      <Container>
        {headings.map((heading, index) => (
          <React.Fragment key={index}>
            <Header>{heading.header}</Header>
            {/* <SubHeader>{heading.subHeader}</SubHeader> */}
          </React.Fragment>
        ))}

        {/* LOADING MESSAGE ADDED HERE */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            pointerEvents: 'none', // Allows clicking "through" the loader if needed
          }}
        >
          {isLayoutLoading && (
            <LoadingComponentScalable
              borderWidth={5}
              containerSize={80}
              message={t('please_wait_while_we_load_some_initial_data')}
              initialSeconds={15}
            />
          )}
        </div>

        <BoxMenuContainer>
          <BoxMenuRow>
            {displaySettings && (
              <StyledIcon
                name={'navigate_before'}
                className="box-icon"
                onClick={() => navigateFromBox('back')}
                style={{ fontSize: '2.5vw' }}
              />
            )}
          </BoxMenuRow>
          {!displaySettings ? (
            <>
              <BoxMenuRow>{renderBoxMenus(boxMenuConfig, 0, 6)}</BoxMenuRow>
              <BoxMenuRow>{renderBoxMenus(boxMenuConfig, 6, 6)}</BoxMenuRow>
            </>
          ) : (
            <>
              <BoxMenuRow>{renderBoxMenus(boxMenuSettingsConfig, 0, 3)}</BoxMenuRow>
              <BoxMenuRow>{renderBoxMenus(boxMenuSettingsConfig, 3, 3)}</BoxMenuRow>
            </>
          )}
        </BoxMenuContainer>
      </Container>
    </ThemeProvider>
  );
};

export default MainHome;
